import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { newSourceUrl } from "../../../../api/Source.api";
import { button_bg_color_for_New_Project } from "../../../../constants/colors";

export default function VideoCreationDialog({
  open,
  onClose,
  videoTitle,
  timelineItems,
  onStart,
  caption,
  Width,
  Height,
}) {
  const [loading, setLoading] = useState(true);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("pending");
  const [statusMessage, setStatusMessage] = useState(
    "Initializing video generation..."
  );

    const theme = localStorage.getItem('theme');

  // Polling function to check video status
  const pollVideoStatus = async (statusId, initialUrl) => {
    const token = localStorage.getItem("token");
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    const maxAttempts = 100; // Max 500 seconds (100 * 5 seconds)
    const pollInterval = 5000; // Poll every 5 seconds
    let attempts = 0;

    return new Promise((resolve, reject) => {
      const checkStatus = async () => {
        try {
          const response = await fetch(
            `${newSourceUrl}//v1/video/avatar/${statusId}/status`,
            {
              headers: {
                "Content-Type": "application/json",
                ...(isLoggedIn && { Authorization: `Bearer ${token}` }),
              },
            }
          );

          if (!response.ok) {
            let errorMessage = "Failed to check video status";
            try {
              const contentType = response.headers.get("content-type");
              if (contentType && contentType.includes("application/json")) {
                const err = await response.json();
                errorMessage =
                  err.message || `Status check failed (${response.status})`;
              } else {
                // Don't show raw HTML/text responses to user
                if (response.status === 500) {
                  errorMessage = "Server error while checking status.";
                } else if (response.status === 404) {
                  errorMessage =
                    "Video status not found. Please try regenerating.";
                } else {
                  errorMessage = `Status check failed (Error ${response.status})`;
                }
                console.error("Non-JSON status error:", await response.text());
              }
            } catch (e) {
              console.error("Error parsing status error response:", e);
              errorMessage = "Failed to check video status";
            }
            throw new Error(errorMessage);
          }

          const statusData = await response.json();

          // Update progress and status
          setProgress(statusData.progress || 0);
          setStatus(statusData.status);

          // Update status message based on progress
          if (statusData.progress < 25) {
            setStatusMessage("Processing your script...");
          } else if (statusData.progress < 50) {
            setStatusMessage("Generating avatar animations...");
          } else if (statusData.progress < 75) {
            setStatusMessage("Rendering video...");
          } else {
            setStatusMessage("Finalizing your video...");
          }

          if (statusData.status === "completed") {
            resolve({
              url: statusData.url || initialUrl,
              status: statusData,
            });
          } else if (statusData.status === "failed") {
            reject(new Error(statusData.error || "Video generation failed"));
          } else if (attempts >= maxAttempts) {
            reject(new Error("Video generation timed out. Please try again."));
          } else {
            // Continue polling
            attempts++;
            setTimeout(checkStatus, pollInterval);
          }
        } catch (error) {
          console.error("Error checking status:", error);
          if (attempts < 3) {
            // Retry a few times for network errors
            attempts++;
            setTimeout(checkStatus, pollInterval);
          } else {
            reject(error);
          }
        }
      };

      // Start polling
      checkStatus();
    });
  };

  useEffect(() => {
    const sendVideoData = async () => {
      if (!open) return;
      setLoading(true);
      setErrorMsg("");
      setDownloadUrl(null);
      setProgress(0);
      setStatus("pending");
      setStatusMessage("Initializing video generation...");

      const payloadItems = timelineItems.map((item) => ({
        avatar: item.avatar,
        talkingphoto: item.talkingphoto,
        voice: item.voice,
        script: item.script,
        background: item.background,
      }));

      const token = localStorage.getItem("token");
      const isLoggedIn = localStorage.getItem("isLoggedIn");

      try {
        // const response = await fetch(`${newSourceUrl}/v1/video/avatar/generate`, {
        const response = await fetch(`https://api.modalx.ai/api/heygen/video`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(isLoggedIn && { Authorization: `Bearer ${token}` }),
          },
          body: JSON.stringify({
            title: videoTitle,
            caption: caption,
            width: Width,
            height: Height,
            items: payloadItems,
          }),
        });

        if (!response.ok) {
          let errorMessage = "Failed to generate video";
          try {
            const contentType = response.headers.get("content-type");
            if (contentType && contentType.includes("application/json")) {
              const err = await response.json();
              errorMessage = err.message || `Server error (${response.status})`;
            } else {
              // Don't show raw HTML/text responses to user
              if (response.status === 500) {
                errorMessage = "Server error. Please try again later.";
              } else if (response.status === 404) {
                errorMessage =
                  "Service not available. Please check your connection.";
              } else if (response.status === 401) {
                errorMessage = "Authentication failed. Please log in again.";
              } else if (response.status === 400) {
                errorMessage = "Invalid request. Please check your input.";
              } else {
                errorMessage = `Request failed (Error ${response.status})`;
              }
              console.error("Non-JSON error response:", await response.text());
            }
          } catch (e) {
            console.error("Error parsing error response:", e);
            errorMessage = "An unexpected error occurred";
          }
          throw new Error(errorMessage);
        }

        const data = await response.json();
        console.log("Initial response:", data);

        // Check if we got the new async response format
        if (data.status_id) {
          // New async API - start polling
          setStatus("processing");
          const result = await pollVideoStatus(
            data.status_id,
            data.url || data.data
          );
          setDownloadUrl(result.url);
          setStatus("completed");
          setStatusMessage("Video ready for download!");
        } else {
          // Old sync API - video is ready immediately
          setDownloadUrl(data.data || data.url);
          setProgress(100);
          setStatus("completed");
          setStatusMessage("Video ready for download!");
        }
      } catch (err) {
        setErrorMsg(err.message);
        setStatus("failed");
        setStatusMessage("Video generation failed");
      } finally {
        setLoading(false);
      }
    };
    sendVideoData();
  }, [open]);

  const handlePrimaryClick = async () => {
    if (downloadUrl) {
      console.log("Opening download URL:", downloadUrl);

      // Test: Open the download link directly in a new tab
      // Check if downloadUrl already has https://
      const fullUrl = downloadUrl.startsWith("http")
        ? downloadUrl
        : `https://${downloadUrl}`;
      window.open(fullUrl, "_blank");

      // COMMENTED OUT FOR TESTING - Original download functionality
      /*
      const filename = videoTitle;
      const token = localStorage.getItem("token");
      const isLoggedIn = localStorage.getItem("isLoggedIn");

      try {
        // Fetch the file as a blob
        const response = await fetch("https://" + downloadUrl, {
          method: "GET",
          headers: {
            "Content-Type": "video/mp4",
            ...(isLoggedIn && { Authorization: `Bearer ${token}` }),
          },
        });
        //const response = await window.__originalFetch(downloadUrl);
        const blob = await response.blob();

        // Create a download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename + ".mp4";
        document.body.appendChild(link);
        link.click();

        // Cleanup
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      } catch (error) {
        console.error("Download failed:", error);
        setErrorMsg(error?.message);
      }
      */
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={
        status === "completed" || status === "failed" ? onClose : undefined
      }
      maxWidth="sm"
      fullWidth
       PaperProps={{
          sx: {
            bgcolor: "#1F233E",
            color: "white",
            borderRadius: "16px",
            margin:"0px"
          },
        }}
    >
      <DialogTitle sx={{
            color: "white",
            fontSize: "18px",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding:"16px 20px",
            borderBottom:"1px solid rgba(255, 255, 255, 0.10)",
            maxHeight:"60px"
          }}>
        {status === "completed"
          ? "Video Ready!"
          : status === "failed"
          ? "Generation Failed"
          : "Creating Video"}
          <IconButton
            onClick={onClose}
            sx={{ color: "#ccc" }}
          >
            <CloseIcon />
          </IconButton>
      </DialogTitle>
      <DialogContent dividers  sx={{display:"flex", justifyContent:"center", margin:"20px 25px", padding:"0px"}}>
        <Box sx={{ minHeight: 50 }}>
          {/* Processing State */}
          {status === "processing" && (
            <Box>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <CircularProgress size={24} />
                <Typography variant="body1" color="rgba(255, 255, 255, 0.80)">{statusMessage}</Typography>
              </Box>

              {/* Progress Bar */}
              <Box sx={{ width: "100%", mb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: "100%" }}>
                    <Box
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: "rgba(0, 161, 255, 0.1)",
                        overflow: "hidden",
                        position: "relative",
                      }}
                    >
                      <Box
                        sx={{
                          height: "100%",
                          width: `${progress}%`,
                          bgcolor: "#00a1ff",
                          borderRadius: 4,
                          transition: "width 0.5s ease-in-out",
                        }}
                      />
                    </Box>
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ minWidth: 45 }}
                  >
                    {progress}%
                  </Typography>
                </Box>
              </Box>

              <Typography variant="caption" color="rgba(255, 255, 255, 0.80)">
                This may take 2-5 minutes depending on video length
              </Typography>
            </Box>
          )}

          {/* Initial Loading State */}
          {status === "pending" && loading && (
            <Box display="flex" alignItems="center" gap={2}>
              <CircularProgress size={24} />
              <Typography>{statusMessage}</Typography>
            </Box>
          )}

          {/* Success State */}
          {status === "completed" && !loading && (
            <Box textAlign="center" py={2}>
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: "50%",
                  bgcolor: "success.light",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2,
                }}
              >
                <Typography fontSize={30}>✓</Typography>
              </Box>
              <Typography variant="h6" gutterBottom>
                Your video is ready!
              </Typography>
              <Typography variant="body2" color="rgba(255, 255, 255, 0.80)">
                Click Download to save your video
              </Typography>
            </Box>
          )}

          {/* Error State */}
          {status === "failed" && (
            <Box textAlign="center" py={2}>
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: "50%",
                  bgcolor: "error.light",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2,
                }}
              >
                <Typography fontSize={30} color="error">
                  ✕
                </Typography>
              </Box>
              <Typography variant="h6" gutterBottom color="error">
                Generation Failed
              </Typography>
              <Typography variant="body2" color="error">
                {errorMsg || "An error occurred during video generation"}
              </Typography>
              <Typography
                variant="caption"
                display="block"
                mt={2}
                color="#ffffff"
              >
                Please try again or contact support if the issue persists
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{display:"flex",padding:"16px 20px", maxHeight:"72px"}}>
        {status === "processing" && (
          <Button onClick={onClose} disabled>
            Processing...
          </Button>
        )}
        {status === "pending" && !errorMsg && (
          <Button onClick={onClose} 
          sx={{
                    width: "103px",
                    height: "40px",
                    textTransform: "none",
                    fontFamily: "Inter",
                    fontSize: "16px",
                    fontWeight: 600,
                    textAlign: "center",
                    bgcolor: "#FFFFFF",
                    color: "#17182C",
                    '&:hover': {
                        backgroundColor: '#CDCFDF'
                      },
              }}
          >Cancel</Button>
        )}
        {status === "failed" && (
          <Button onClick={onClose} color="error" 
           sx={{
                    width: "103px",
                    height: "40px",
                    textTransform: "none",
                    fontFamily: "Inter",
                    fontSize: "16px",
                    fontWeight: 600,
                    textAlign: "center",
                    bgcolor: "#DD2025",
                    color: "#FFFFFF",
                    '&:hover': {
                        backgroundColor: '#E6E0E91F'
                      },
                  }}
          >
            Close
          </Button>
        )}
        {status === "completed" && (
          <>
            <Button onClick={onClose}
             sx={{
                    width: "103px",
                    height: "40px",
                    textTransform: "none",
                    fontFamily: "Inter",
                    fontSize: "16px",
                    fontWeight: 600,
                    textAlign: "center",
                    bgcolor: "#FFFFFF",
                    color: "#17182C",
                    '&:hover': {
                        backgroundColor: '#CDCFDF'
                      },
              }}
            >Close</Button>
            <Button 
              variant="contained"
              color="primary"
              onClick={handlePrimaryClick}
               sx={{backgroundColor:theme === 'dark'? button_bg_color_for_New_Project :'',
                         '&:hover': {
                           backgroundColor:'#FFD9AA',
                         },
                         color:'#17182C',
                         fontSize:'16px',
                         fontWeight:600,
                         textTransform:'none',
                         fontFamily: 'Inter, sans-serif',}}
            >
              View Video
              {/* Original text: Download Video */}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}